import { Dispatch, useEffect } from 'react';
import {
  FacephiSelphiWidget,
  FacephiSelphiWidgetCustomEvent,
  SelphiExtractionFinishEvent,
  SelphiExceptionCapturedEvent,
} from '@facephi/sdk-web-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useLogger } from '../../hooks';
import { useFacialAuth } from '../../providers';
import { RoutesName, antispoofingState } from '../../states';

type FaceWidgetProps = {
  setLoading: Dispatch<boolean>;
  transfer: boolean;
};

export const FaceWidget = ({ setLoading, transfer }: FaceWidgetProps) => {
  const { i18n } = useTranslation();
  const antispoofing = useRecoilValue(antispoofingState);
  const navigate = useNavigate();
  const { onSaveFacial, onValidateSpoof, onSaveUserPicture } = useFacialAuth();

  const { captureMessage, captureException } = useLogger();

  useEffect(() => {
    if (process.env.REACT_APP_TEST && transfer) {
      onExtractionFinish({
        detail: { detail: { extractionData: { bestImageTokenized: 'face' } } },
      } as FacephiSelphiWidgetCustomEvent<SelphiExtractionFinishEvent>);
    }
  }, []);

  const onExtractionFinish = async (
    event: FacephiSelphiWidgetCustomEvent<SelphiExtractionFinishEvent>
  ) => {
    setLoading(true);
    try {
      if (transfer) {
        // Facial authentication is made in backend with SDK data
        if (antispoofing) {
          const responseAntispoof = await onValidateSpoof(
            event.detail?.detail?.extractionData.encryptedLivenessRaw
          );
          if (!responseAntispoof) {
            navigate(RoutesName.transferDenied);
          }
        }
      } else {
        onSaveFacial(event?.detail?.detail?.extractionData.bestImageTokenized);
        onSaveUserPicture(event?.detail?.detail?.extractionData.bestImage.data);
      }
    } catch (error) {
      captureException(error as Error);
      if (transfer) {
        navigate(RoutesName.transferError);
      } else {
        navigate(RoutesName.deniedRegister);
      }
    }
  };
  const onExtractionTimeout = () => {
    if (transfer) {
      navigate(RoutesName.transferDenied);
    } else {
      navigate(RoutesName.deniedRegister);
    }
  };

  const onUserCancel = () => {
    if (transfer) {
      navigate(RoutesName.transferDenied);
    } else {
      navigate(RoutesName.deniedRegister);
    }
  };

  const onTimeoutFinish = () => {
    navigate(RoutesName.home);
  };

  const onExceptionCaptured = (
    event: FacephiSelphiWidgetCustomEvent<SelphiExceptionCapturedEvent>
  ) => {
    captureMessage(`Selphi Widget Exception: ${event.detail?.detail?.message}`);
    if (transfer) {
      navigate(RoutesName.transferError);
    } else {
      navigate(RoutesName.deniedRegister);
    }
  };

  return (
    <FacephiSelphiWidget
      className="sdk-widget selphi-widget"
      bundlePath={`${process.env.PUBLIC_URL}selphi`}
      language={i18n.language.split('-')[0]}
      tutorial={false}
      interactible={false}
      cropFactor={1.7}
      previewCapture={false}
      antispoofEnabled={antispoofing}
      advancedTracking={false}
      stabilizationStage={true}
      timeout={60000}
      onExtractionFinish={onExtractionFinish}
      onUserCancel={onUserCancel}
      onExtractionTimeout={onExtractionTimeout}
      onErrorTimeout={onExtractionTimeout}
      onTimeoutErrorButtonClick={onTimeoutFinish}
      onExceptionCaptured={onExceptionCaptured}
    />
  );
};
